import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import GppGoodOutlinedIcon from '@mui/icons-material/GppGoodOutlined';
import { Trans, useTranslation } from 'react-i18next';
import BizCoverLogo from '../../../assets/svg/bizcover-logo.svg?react';
import { EXTERNAL_LINKS } from '../constants/external_urls';
import BizCoverIllustration from './BizCoverIllustration';
import { BizCoverBannerProps } from './types';

const COVER_TYPES = [
  'coverPublicLiability',
  'coverProfessionalIndemnity',
  'coverCyberLiability',
  'coverManagementLiability',
] as const;

const BizCoverBanner: React.FC<BizCoverBannerProps> = ({
  variant = 'card',
  href = EXTERNAL_LINKS.BIZCOVER,
  id = 'bizCoverBanner',
  className,
}) => {
  const { t } = useTranslation();
  const isCard = variant === 'card';
  const isDashboard = variant === 'dashboard';
  const headingId = `${id}Heading`;

  const cta = (
    <Button
      id={`${id}CtaBtn`}
      variant='contained'
      component='a'
      href={href}
      target='_blank'
      rel='noopener noreferrer sponsored'
      className='bizcover-banner__cta-button'
      endIcon={<OpenInNewIcon aria-hidden='true' />}
    >
      {t(isCard ? 'bizCover.ctaButton' : 'bizCover.optionsCtaButton')}
      <span className='bizcover-banner__sr-only'>{` (${t('bizCover.opensInNewTab')})`}</span>
    </Button>
  );

  const logo = (
    <Box className='bizcover-banner__logo'>
      <BizCoverLogo role='img' aria-label='BizCover' />
    </Box>
  );

  const rootClassName = ['bizcover-banner', `bizcover-banner--${variant}`, className]
    .filter(Boolean)
    .join(' ');

  // ---------- Variant 1: card (unchanged layout) ----------
  if (isCard) {
    return (
      <Box id={id} component='section' aria-labelledby={headingId} className={rootClassName}>
        <BizCoverIllustration tone='blue' className='bizcover-banner__illustration' />
        <Box className='bizcover-banner__content'>
          {logo}
          <Typography id={headingId} variant='h2' className='bizcover-banner__headline'>
            {t('bizCover.cardHeadline')}
          </Typography>
          <Typography variant='body2' className='bizcover-banner__subtext'>
            {t('bizCover.body')}
          </Typography>
          {cta}
          <Typography className='bizcover-banner__disclosure'>
            {t('bizCover.disclosure')}
          </Typography>
        </Box>
      </Box>
    );
  }

  // ---------- Variants 2 & 3: wide / dashboard ----------
  return (
    <Box
      id={id}
      component='section'
      aria-labelledby={headingId}
      className={rootClassName}
      sx={{ marginTop: isDashboard ? '16px' : 0 }}
    >
      <Box className='bizcover-banner__inner'>
        {/*
          Brand block comes first in the DOM so it always sits at the top on narrow screens.
          On wider containers the grid moves it to the right-hand column.
        */}
        <Box className='bizcover-banner__brand'>
          {logo}
          <BizCoverIllustration
            tone={isDashboard ? 'blue' : 'purple'}
            className='bizcover-banner__illustration'
          />
        </Box>

        <Box className='bizcover-banner__content'>
          <Box className='bizcover-banner__section-title'>
            <GppGoodOutlinedIcon aria-hidden='true' />
            <span>{t('bizCover.sectionTitle')}</span>
          </Box>
          <Typography id={headingId} variant='h2' className='bizcover-banner__headline'>
            {t('bizCover.optionsHeadline')}
          </Typography>
          <Typography variant='body2' className='bizcover-banner__subtext'>
            {t(isDashboard ? 'bizCover.dashboardBody' : 'bizCover.wideBody')}
          </Typography>

          {isDashboard && (
            <Box
              component='ul'
              className='bizcover-banner__chips'
              aria-label={t('bizCover.coverTypesLabel')}
            >
              {COVER_TYPES.map((key) => (
                <li key={key} className='bizcover-banner__chip'>
                  {t(`bizCover.${key}`)}
                </li>
              ))}
            </Box>
          )}

          {cta}

          <Typography className='bizcover-banner__disclosure'>
            <Trans i18nKey='bizCover.partnerDisclosure' components={{ strong: <strong /> }} />
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default BizCoverBanner;
